import React from 'react';
import { useFormContext, useWatch } from '@isyc-react/form/modules/rhf';
import { RhfInputText } from '@isyc-react/form/modules/input/text';
import { RhfInputEmail } from '@isyc-react/form/modules/input/email';
import { RhfInputDate } from '@isyc-react/form/modules/input/date';
import { RhfInputPhone } from '@isyc-react/form/modules/input/phone';
import { RhfInputSelectIntl } from '@isyc-react/form/modules/input/select';
import { namespaces } from 'i18n/i18n.constants';
import { useTranslation } from 'react-i18next';
import { currentDate, dateFormatString, genericMinDate, numberFormatString, personSexOptions } from 'common-app/options';
import { getParameterName, RhfInputBailleursParameter, RhfInputBailleursParameterBooleanYesNo, RhfInputBailleursParameterSelectCheckbox, } from 'common-app/parameter';
import { getFutureDateAllowed, getIsLegalAgeDateAllowed, getIsMinDateAllowed, useIsAdult, } from 'common-app/types/bailleurs-utils';
import { familySituationCodeSingle } from './signatory-form.mapper';
import clsx from 'clsx';
import { useDossierContext } from 'common-app/dossier';
import { RhfInputCommune } from 'common-app/parameter/input-parameter-balleurs-select.component';
import { validNameSurname } from 'common/utils';
export const SignatoryForm = props => {
    const { name } = props;
    const { userHasSecurityToken } = useDossierContext();
    const { control, getValues, resetField } = useFormContext();
    const { t } = useTranslation(namespaces.common);
    const paramName = getParameterName(name);
    const watchHousing = useWatch({ name: paramName('toujours_dans_logement') });
    const watchFamilySituationCode = useWatch({ name: paramName('code_situation_familiale') });
    const watchBirthDate = useWatch({ name: `${name}.birthDate` });
    const watchCountry = useWatch({ name: paramName('paysnaiss') });
    const watchDepartament = useWatch({ name: paramName('deptnaiss') });
    const countrySelectedFrance = React.useMemo(() => {
        if ((watchCountry === null || watchCountry === void 0 ? void 0 : watchCountry.value) === '')
            return undefined;
        if ((watchCountry === null || watchCountry === void 0 ? void 0 : watchCountry.value) === '99100') {
            return true;
        }
        else {
            return false;
        }
    }, [watchCountry]);
    const { initialFamilySituationCode, initialFamilySituationDate } = React.useMemo(() => {
        const initialFamilySituationCode = getValues(paramName('code_situation_familiale'));
        const initialFamilySituationDate = getValues(paramName('date_situation_familiale'));
        return { initialFamilySituationCode, initialFamilySituationDate };
    }, []);
    const isAdult = useIsAdult(watchBirthDate, dateFormatString);
    return (React.createElement(React.Fragment, null,
        React.createElement("h2", { className: 'bg-light font-size-14 text-black fw-700 bg-tertiary p-2 text-center mt-4 text-uppercase' }, "Informations"),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    t('bailleurs.core.person.form.label.sex'),
                    " ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputSelectIntl, { rhf: { control, name: `${name}.sex`, rules: { required: true } }, input: {
                        namespace: namespaces.common,
                        isSearchable: false,
                        isClearable: true,
                        options: personSexOptions,
                    } }))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    "Nom ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.surname`,
                        rules: {
                            required: true,
                            validate: {
                                validChars: surname => {
                                    return validNameSurname(surname);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    "Pr\u00E9nom ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.firstname`,
                        rules: {
                            required: true,
                            validate: {
                                validChars: firstname => {
                                    return validNameSurname(firstname);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' }, t('bailleurs.core.person.form.label.birthSurname')),
                React.createElement(RhfInputText, { rhf: {
                        control,
                        name: `${name}.birthName`,
                        rules: {
                            validate: {
                                validChars: birthName => {
                                    return validNameSurname(birthName);
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validChars')
                            return t('bailleurs.core.person.form.error.charsError');
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' },
                    t('bailleurs.core.person.form.label.birthDate'),
                    " ",
                    React.createElement("span", { className: 'required' }, "*")),
                React.createElement(RhfInputDate, { rhf: {
                        control,
                        name: `${name}.birthDate`,
                        rules: {
                            required: true,
                            validate: {
                                minYearAvailable: value => getIsMinDateAllowed(value),
                                maxYearAvailable: value => getIsLegalAgeDateAllowed(value),
                            },
                        },
                    }, input: {
                        placeholder: t('bailleurs.core.form.placeholder.date'),
                        dateFormat: dateFormatString,
                        numberFormat: numberFormatString,
                        minDate: genericMinDate,
                        maxDate: currentDate,
                    } }))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' }, "Adresse email"),
                React.createElement(RhfInputEmail, { rhf: { control, name: `${name}.mail` }, errorMessage: error => {
                        switch (error.type) {
                            case 'valid':
                                return React.createElement(React.Fragment, null, "Format d\u2019adresse non valide");
                            default:
                                return undefined;
                        }
                    } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' }, "T\u00E9l\u00E9phone fixe"),
                React.createElement(RhfInputPhone, { rhf: { control, name: `${name}.personalPhone` }, input: { defaultCountry: 'FR' } })),
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement("label", { className: 'form-label' }, "T\u00E9l\u00E9phone mobile"),
                React.createElement(RhfInputPhone, { rhf: { control, name: `${name}.mobilePhone` }, input: { defaultCountry: 'FR' } }))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('paysnaiss'), rules: { required: userHasSecurityToken } } })),
            countrySelectedFrance !== undefined && (React.createElement(React.Fragment, null, countrySelectedFrance ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: clsx('col-12 col-lg-6 col-xxl-4 mb-3') },
                    React.createElement(RhfInputBailleursParameter, { rhf: {
                            control,
                            name: paramName('deptnaiss'),
                            rules: { required: userHasSecurityToken },
                        }, key: 'deptnaiss' })),
                React.createElement("div", { className: clsx('col-12 col-lg-6 col-xxl-4 mb-3') },
                    React.createElement(RhfInputCommune, { rhf: {
                            control,
                            name: paramName('commnaiss'),
                            rules: { required: userHasSecurityToken },
                        }, departament: watchDepartament, key: 'commnaiss' })))) : (React.createElement("div", { className: clsx('col-12 col-lg-6 col-xxl-4 mb-3') },
                React.createElement(RhfInputBailleursParameter, { rhf: {
                        control,
                        name: paramName('commnaiss_etranger'),
                        rules: {
                            required: userHasSecurityToken,
                            validate: {
                                validateLength: (value) => {
                                    const villeValue = value.value;
                                    return villeValue.length <= 25;
                                },
                            },
                        },
                    }, errorMessage: error => {
                        if (error.type === 'validateLength') {
                            return 'Maximum 25 caractères';
                        }
                    } })))))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('code_nationalite'), rules: { required: true } } }))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-4' },
                React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('code_situation_familiale'), rules: { required: true } } })),
            watchFamilySituationCode.value !== familySituationCodeSingle && (React.createElement(React.Fragment, null, (initialFamilySituationDate.value ||
                (watchFamilySituationCode.value &&
                    watchFamilySituationCode.value !== familySituationCodeSingle &&
                    watchFamilySituationCode.value !== initialFamilySituationCode.value)) && (React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('date_situation_familiale'), rules: { required: true } } })))))),
        React.createElement("div", { className: 'row' },
            React.createElement("div", { className: 'col-12 mb-1 d-flex align-items-end logement' },
                React.createElement(RhfInputBailleursParameterBooleanYesNo, { rhf: { control, name: paramName('toujours_dans_logement') } })),
            watchHousing.value === 'false' && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameter, { rhf: {
                            control,
                            name: paramName('parti_le'),
                            rules: {
                                required: true,
                                validate: {
                                    futureDateAllowed: dateParam => getFutureDateAllowed(dateParam.value),
                                },
                            },
                        } })),
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('motif_depart'), rules: { required: true } } }))))),
        React.createElement("div", { className: 'row my-3' },
            React.createElement("div", { className: 'col-12' },
                React.createElement(RhfInputBailleursParameterBooleanYesNo, { rhf: { control, name: paramName('handicap') }, input: { label: React.createElement(React.Fragment, null, "\u00CAtes-vous titulaire d'une carte d'invalidit\u00E9 ou d'une CMI mention invalidit\u00E9 ?") } }))),
        isAdult && (React.createElement(React.Fragment, null,
            React.createElement("h2", { className: 'bg-light font-size-14 text-black fw-700 bg-tertiary p-2 text-center mt-4 text-uppercase' }, "Activit\u00E9 et revenus"),
            React.createElement("div", { className: 'row' },
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameter, { rhf: { control, name: paramName('code_csp'), rules: { required: true } } }))),
            React.createElement("div", { className: 'row my-4' },
                React.createElement("label", { className: 'form-label mb-3' },
                    "Nature des revenus ",
                    React.createElement("small", { className: 'fw-normal fst-italic' }, "(plusieurs choix possibles)"),
                    ":"),
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameterSelectCheckbox, { rhf: { control, name: paramName('Individu_Prestations') } })),
                React.createElement("div", { className: 'col-12 col-lg-6 col-xxl-4 mb-3' },
                    React.createElement(RhfInputBailleursParameterSelectCheckbox, { rhf: { control, name: paramName('Individu_Ressources') } })))))));
};
// TODO update
export const SignatorySummary = props => {
    const { value } = props;
    return (React.createElement(React.Fragment, null,
        React.createElement("small", { className: 'd-flex align-items-center' },
            (value.firstname || value.surname) && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'me-2' },
                    value.firstname,
                    " ",
                    value.surname))),
            value.birthName && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'text-muted me-2' }, "|"),
                React.createElement("div", { className: 'me-2' }, value.birthName))),
            value.birthDate && (React.createElement(React.Fragment, null,
                React.createElement("div", { className: 'text-muted me-2' }, "|"),
                React.createElement("div", { className: 'me-2' }, value.birthDate))))));
};
